import React, { useState, useEffect } from 'react';
import { SistematizacionResponse, CustomerValidationResponse, CreateCustomerRequest } from '../../types/sistematizacion.types';
import paymentService from '../../services/paymentService';
import customerService from '../../services/customerService';
import { formatCurrency } from '../../utils/formatters';
import './PaymentModal.css';

// Declarar el tipo para el script de Bold
declare global {
  interface Window {
    BoldCheckout?: any;
  }
}

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  deudaData: SistematizacionResponse[];
  totalDeuda: number;
  placa: string;
}

type PaymentStep = 'customer-lookup' | 'customer-info' | 'creating-link' | 'redirect-ready' | 'error';

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  deudaData,
  totalDeuda,
  placa,
}) => {
  const [step, setStep] = useState<PaymentStep>('customer-lookup');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Customer lookup
  const [customerIdentification, setCustomerIdentification] = useState<string>('');
  const [customerValidated, setCustomerValidated] = useState<CustomerValidationResponse | null>(null);

  // Customer form (editable después de validación)
  const [customerName, setCustomerName] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerAddress, setCustomerAddress] = useState<string>('');
  const [customerCity, setCustomerCity] = useState<string>('');

  // Bold payment data
  const [paymentUrl, setPaymentUrl] = useState<string>('');
  const [orderId, setOrderId] = useState<string>('');
  const [boldButtonLoaded, setBoldButtonLoaded] = useState(false);

  // Cargar y renderizar botón de Bold
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    
    if (step === 'redirect-ready' && paymentUrl) {
      setBoldButtonLoaded(false);
      
      // Usar setTimeout para asegurar que el DOM esté listo
      timer = setTimeout(() => {
        const container = document.getElementById('bold-button-wrapper');
        if (!container) return;
        
        // Limpiar contenedor de forma segura
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
        
        try {
          // Extraer parámetros de la URL
          const url = new URL(paymentUrl);
          const params = url.searchParams;
          
          // ============================================
          // CORRECCIÓN: Usar los nombres correctos de parámetros
          // El backend envía: apiKey, orderId, integritySignature (camelCase)
          // ============================================
          const apiKey = params.get('apiKey') || '';
          const orderIdParam = params.get('orderId') || '';
          const amount = params.get('amount') || '';
          const currency = params.get('currency') || 'COP';
          const integritySignature = params.get('integritySignature') || '';
          const description = params.get('description') ? decodeURIComponent(params.get('description')!) : `Sistematizacion vehiculo ${placa}`;
          const redirectionUrl = params.get('redirectionUrl') ? decodeURIComponent(params.get('redirectionUrl')!) : '';
          const customerData = params.get('customerData') ? decodeURIComponent(params.get('customerData')!) : '';
          
          // Log para debug
          console.log('Bold Button Params:', {
            apiKey: apiKey ? '***' + apiKey.slice(-8) : 'EMPTY!',
            orderIdParam,
            amount,
            integritySignature: integritySignature ? integritySignature.slice(0, 16) + '...' : 'EMPTY!',
            redirectionUrl
          });

          // Validar que tenemos los parámetros requeridos
          if (!apiKey) {
            console.error('ERROR: apiKey está vacío!');
            console.log('URL recibida:', paymentUrl);
            console.log('Parámetros disponibles:', Array.from(params.keys()));
          }
          
          // Crear el script del botón con src para que Bold lo procese
          const boldScript = document.createElement('script');
          boldScript.src = 'https://checkout.bold.co/library/boldPaymentButton.js';
          boldScript.setAttribute('data-bold-button', 'dark-L');
          boldScript.setAttribute('data-api-key', apiKey);
          boldScript.setAttribute('data-order-id', orderIdParam);
          boldScript.setAttribute('data-amount', amount);
          boldScript.setAttribute('data-currency', currency);
          boldScript.setAttribute('data-integrity-signature', integritySignature);
          boldScript.setAttribute('data-description', description);
          boldScript.setAttribute('data-redirection-url', redirectionUrl);
          
          if (customerData) {
            boldScript.setAttribute('data-customer-data', customerData);
          }
          
          container.appendChild(boldScript);
          
          // Dar tiempo para que Bold procese el script
          setTimeout(() => {
            setBoldButtonLoaded(true);
          }, 1500);
          
        } catch (e) {
          console.error('Error creating Bold button:', e);
        }
      }, 100);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [step, paymentUrl, placa]);

  // Validar cliente por identificación
  const handleValidateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerIdentification.trim()) {
      setError('Por favor ingrese el número de identificación');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await customerService.validateCustomer(customerIdentification.trim());

      if (response.Success && response.Data) {
        const customer = response.Data;
        setCustomerValidated(customer);

        if (customer.Exists) {
          // Cliente existe en SAP - prellenar datos
          setCustomerName(customer.CustomerName || '');
          setCustomerEmail(customer.Email || '');
          setCustomerPhone(customer.Phone || '');
          
          if (customer.IsBlocked) {
            setError(`Cliente bloqueado: ${customer.BlockReason || 'Contacte al administrador'}`);
            setStep('error');
          } else {
            // Cliente válido, pasar a confirmar datos
            setStep('customer-info');
          }
        } else {
          // Cliente no existe - mostrar mensaje y permitir continuar con datos manuales
          setError('Cliente no encontrado en el sistema. Por favor complete sus datos para continuar.');
          setStep('customer-info');
        }
      } else {
        setError(response.Message || 'Error al validar cliente');
      }
    } catch (err: any) {
      setError('Error al validar cliente: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePaymentLink = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que tengamos identificación
    if (!customerIdentification.trim()) {
      setError('La identificación es requerida para procesar el pago');
      return;
    }

    // Validar datos requeridos si el cliente no existe
    if (!customerValidated?.Exists) {
      if (!customerName.trim()) {
        setError('El nombre es requerido para crear el cliente');
        return;
      }
      if (!customerEmail.trim()) {
        setError('El email es requerido para crear el cliente');
        return;
      }
      if (!customerPhone.trim()) {
        setError('El teléfono es requerido para crear el cliente');
        return;
      }
      if (!customerAddress.trim()) {
        setError('La dirección es requerida para crear el cliente');
        return;
      }
      if (!customerCity.trim()) {
        setError('La ciudad es requerida para crear el cliente');
        return;
      }
    }

    setLoading(true);
    setError('');
    setStep('creating-link');

    try {
      // Si el cliente no existe, crearlo primero en SAP B1
      if (!customerValidated?.Exists) {
        console.log('Cliente no existe, creando en SAP B1...');

        const createRequest: CreateCustomerRequest = {
          Identification: customerIdentification.trim(),
          IdentificationType: 'CC',
          Name: customerName.trim(),
          Email: customerEmail.trim(),
          Phone: customerPhone.trim(),
          Address: customerAddress.trim(),
          City: customerCity.trim(),
          State: 'Antioquia',
          Country: 'CO'
        };

        const createResponse = await customerService.createCustomer(createRequest);

        if (!createResponse.Success || !createResponse.Data) {
          setError(
            createResponse.Message || 'Error al crear el cliente. Por favor intente nuevamente.'
          );
          setStep('error');
          setLoading(false);
          return;
        }

        console.log('Cliente creado exitosamente:', createResponse.Data.CardCode);

        setCustomerValidated({
          Exists: true,
          CardCode: createResponse.Data.CardCode,
          CustomerName: createResponse.Data.CustomerName,
          Email: customerEmail.trim(),
          Phone: customerPhone.trim(),
          RequiresRegistration: false,
          Message: 'Cliente creado exitosamente'
        });
      }

      // Crear el link de pago
      const response = await paymentService.createBoldPaymentLink({
        placa: placa,
        monto: totalDeuda,
        customerName: customerName || undefined,
        customerEmail: customerEmail || undefined,
        customerPhone: customerPhone || undefined,
        customerIdentification: customerIdentification.trim(),
      });

      if (response.Success && response.Data) {
        const paymentUrl = response.Data.PaymentUrl;
        
        // ✅ Si es un link directo de Bold API, redirigir directamente
        if (paymentUrl.includes('/payment/LNK_') || paymentUrl.includes('checkout.bold.co/LNK_')) {
          console.log('✅ Link directo de Bold detectado, redirigiendo...', paymentUrl);
          window.location.href = paymentUrl;
          return;
        }
        
        // Fallback: URL antigua con parámetros (ya no debería ocurrir)
        setPaymentUrl(paymentUrl);
        setOrderId(response.Data.OrderId);
        setStep('redirect-ready');
      } else {
        setError(
          response.Message || 'Error al crear el link de pago. Por favor intente nuevamente.'
        );
        setStep('error');
      }
    } catch (err: any) {
      setError('Error al procesar la solicitud: ' + (err.message || 'Error desconocido'));
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset state
    setStep('customer-lookup');
    setCustomerIdentification('');
    setCustomerValidated(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomerAddress('');
    setCustomerCity('');
    setPaymentUrl('');
    setOrderId('');
    setError('');
    setBoldButtonLoaded(false);
    onClose();
  };

  const handleBackToLookup = () => {
    setStep('customer-lookup');
    setCustomerValidated(null);
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomerAddress('');
    setCustomerCity('');
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content payment-modal">
        <div className="modal-header">
          <h2>Pago de Sistematización - {placa}</h2>
          <button className="modal-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Resumen de deuda */}
          <div className="payment-summary">
            <h3>Resumen de Deuda</h3>
            <ul className="debt-list">
              {deudaData
                .filter((item) => item.ValorPorFacturar && item.ValorPorFacturar > 0)
                .map((item, index) => (
                  <li key={index}>
                    <span>{item.DescripcionNoFacturada}</span>
                    <span>{formatCurrency(item.ValorPorFacturar || 0)}</span>
                  </li>
                ))}
            </ul>
            <div className="debt-total">
              <strong>Total a Pagar:</strong>
              <strong className="total-amount">{formatCurrency(totalDeuda)}</strong>
            </div>
          </div>

          {/* Customer Lookup Step - NUEVO: Primero validar identificación */}
          {step === 'customer-lookup' && (
            <div className="payment-step">
              <h3>Identificación del Cliente</h3>
              <p className="step-description">
                Ingrese su número de identificación para verificar sus datos en el sistema.
              </p>
              <form onSubmit={handleValidateCustomer}>
                <div className="form-group">
                  <label htmlFor="customerIdentification">Número de Identificación *</label>
                  <input
                    type="text"
                    id="customerIdentification"
                    value={customerIdentification}
                    onChange={(e) => setCustomerIdentification(e.target.value)}
                    placeholder="Ingrese su número de cédula o NIT"
                    required
                    autoFocus
                  />
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="modal-actions">
                  <button type="button" onClick={handleClose} className="btn btn-secondary">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Verificando...' : 'Verificar Cliente'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Customer Info Step - Confirmar/Completar datos */}
          {step === 'customer-info' && (
            <div className="payment-step">
              <h3>Confirmar Datos del Cliente</h3>
              {customerValidated?.Exists ? (
                <div className="customer-found-message">
                  <span className="success-icon">✓</span>
                  <p>Cliente encontrado: <strong>{customerValidated.CustomerName}</strong></p>
                </div>
              ) : (
                <div className="customer-not-found-message">
                  <span className="warning-icon">⚠</span>
                  <p>Cliente no registrado. Complete los datos para continuar.</p>
                </div>
              )}
              <form onSubmit={handleCreatePaymentLink}>
                <div className="form-group">
                  <label htmlFor="customerIdentificationDisplay">Identificación</label>
                  <input
                    type="text"
                    id="customerIdentificationDisplay"
                    value={customerIdentification}
                    disabled
                    className="input-disabled"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerName">Nombre Completo *</label>
                  <input
                    type="text"
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Ingrese su nombre completo"
                    required={!customerValidated?.Exists}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerEmail">Correo Electrónico {!customerValidated?.Exists && '*'}</label>
                  <input
                    type="email"
                    id="customerEmail"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    required={!customerValidated?.Exists}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPhone">Teléfono {!customerValidated?.Exists && '*'}</label>
                  <input
                    type="tel"
                    id="customerPhone"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="3001234567"
                    required={!customerValidated?.Exists}
                  />
                </div>

                {!customerValidated?.Exists && (
                  <>
                    <div className="form-group">
                      <label htmlFor="customerAddress">Dirección *</label>
                      <input
                        type="text"
                        id="customerAddress"
                        value={customerAddress}
                        onChange={(e) => setCustomerAddress(e.target.value)}
                        placeholder="Calle 123 # 45-67"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="customerCity">Ciudad *</label>
                      <input
                        type="text"
                        id="customerCity"
                        value={customerCity}
                        onChange={(e) => setCustomerCity(e.target.value)}
                        placeholder="La Estrella"
                        required
                      />
                    </div>
                  </>
                )}

                {error && !customerValidated?.Exists && (
                  <div className="warning-message">{error}</div>
                )}

                <div className="modal-actions">
                  <button type="button" onClick={handleBackToLookup} className="btn btn-secondary">
                    ← Cambiar Identificación
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Generando...' : 'Continuar al Pago'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Creating Link Step */}
          {step === 'creating-link' && (
            <div className="payment-step">
              <div className="processing-indicator">
                <div className="spinner"></div>
                <p>
                  {!customerValidated?.Exists
                    ? 'Creando cliente y generando link de pago...'
                    : 'Generando link de pago...'}
                </p>
                <p className="processing-detail">Por favor espere mientras preparamos su pago.</p>
              </div>
            </div>
          )}

          {/* Redirect Ready Step */}
          {step === 'redirect-ready' && (
            <div className="payment-step">
              <div className="success-message">
                <div className="success-icon">💳</div>
                <h3>¡Listo para Pagar!</h3>
                <p>Haga clic en el botón de Bold para completar su pago de forma segura.</p>
                <div className="payment-details">
                  <p>
                    <strong>Orden:</strong> {orderId}
                  </p>
                  <p>
                    <strong>Monto:</strong> {formatCurrency(totalDeuda)}
                  </p>
                </div>
                
                {/* Contenedor del botón de Bold - usando ID para evitar conflictos con React */}
                <div className="bold-button-container">
                  <div id="bold-button-wrapper"></div>
                  {!boldButtonLoaded && (
                    <div className="bold-loading">
                      <div className="spinner"></div>
                      <p>Cargando botón de pago...</p>
                    </div>
                  )}
                </div>
                
                <div className="modal-actions modal-actions-stacked">
                  <button onClick={handleClose} className="btn btn-link">
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="payment-step">
              <div className="error-message-box">
                <div className="error-icon">✕</div>
                <h3>Error al Generar Link de Pago</h3>
                <p>{error}</p>
                <div className="modal-actions">
                  <button onClick={handleClose} className="btn btn-secondary">
                    Cerrar
                  </button>
                  <button
                    onClick={() => setStep('customer-info')}
                    className="btn btn-primary"
                  >
                    Intentar Nuevamente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;